import { useCallback, useEffect, useState } from "react";

/**
 * Chrome's own install prompt, surfaced as a button.
 *
 * Chrome fires `beforeinstallprompt` when it judges the site installable, and
 * the event is the only way to trigger the real install - the one that produces
 * a WebAPK rather than a bookmark. It can only be used from a user gesture, and
 * only once, so the event is held until the button is pressed.
 *
 * Nothing is rendered when the event never arrives. That covers iOS, where
 * Safari has no such API and the manual steps are the only path, and it covers
 * a Chrome that has decided the site is not installable - offering a button
 * that silently does nothing would be worse than offering none.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallState = "unavailable" | "available" | "installed";

/** True when the page is already running as an installed app. */
function runningStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // iOS Safari predates display-mode and reports it here instead.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function useInstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(runningStandalone);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      // Chrome would otherwise show its own mini-infobar; taking the event lets
      // the page decide where the button lives.
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // The event is single-use: Chrome will fire a fresh one if the user
    // dismisses and remains eligible, so the stale one is dropped either way.
    setPrompt(null);
    if (outcome === "accepted") setInstalled(true);
  }, [prompt]);

  const state: InstallState = installed
    ? "installed"
    : prompt
      ? "available"
      : "unavailable";

  return { state, install };
}
